/**
 * SRES · GIS · shared collar-validation TanStack Query hook.
 *
 * Both GISStudio (for the "X issues" pill badge) and CollarValidator
 * (for the KPI cards) subscribe to the SAME query so:
 *   • The API is hit at most once per (pid, nearTol) even when the
 *     user hops between Boundary ↔ Validation tabs (queryKey cache).
 *   • The badge can render without mounting the CollarValidator panel.
 *
 * The `enabled` flag lets consumers opt in — GISStudio always polls
 * lightly, CollarValidator refreshes on demand via the mutation.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const STALE_MS = 60_000;      // 1 minute — matches typical review pace
const REFRESH_MS = 90_000;     // background refresh cadence

export function useCollarValidationQuery(pid, nearTol = 25, opts = {}) {
  return useQuery({
    queryKey: ["collar-validation", pid, nearTol],
    queryFn:  () => api.post(
      `/projects/${pid}/gis/collars/validate?near_tolerance_m=${nearTol}`
    ).then((r) => r.data),
    enabled: !!pid && opts.enabled !== false,
    staleTime: STALE_MS,
    refetchInterval: opts.refetchInterval ?? REFRESH_MS,
    refetchOnWindowFocus: false,
    ...opts,
  });
}

/**
 * Roll up the seven-status summary into a single "issues" scalar the
 * pill badge can bind to. Follows the same buckets the Validation
 * panel surfaces:
 *   outside · invalid_coordinate · crs_unknown · transformation_failed
 * are all treated as *issues*. `near_boundary` and `on_boundary` are
 * warnings only — not counted.
 */
export function collarIssueCount(result) {
  const s = result?.summary || {};
  return (
    Number(s.outside               || 0) +
    Number(s.invalid_coordinate    || 0) +
    Number(s.crs_unknown           || 0) +
    Number(s.transformation_failed || 0)
  );
}
